import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  MoreVertical, 
  Eye, 
  CheckCircle, 
  XCircle, 
  Shield, 
  ShieldOff,
  Phone,
  Mail,
  MapPin,
  Star,
  CalendarDays,
  Clock as ClockIcon,
  CheckSquare,
  Square,
  ChevronDown,
  ChevronUp,
  ArrowUpDown,
  X,
  UserCheck,
  UserX,
  Briefcase,
  Award,
  FileText,
  DollarSign,
  AlertTriangle,
  Edit,
  Trash2,
  Download,
  Upload,
  RefreshCw,
  Plus,
  Minus,
  Zap,
  Target,
  TrendingUp,
  TrendingDown,
  Users as UsersIcon,
  BookOpen,
  MessageSquare,
  Calendar,
  CreditCard,
  UserPlus
} from 'lucide-react';
import toast from 'react-hot-toast';

// Users Tab Component
export const UsersTab = ({
  users,
  searchTerm,
  setSearchTerm,
  filterType,
  setFilterType,
  sortBy,
  sortOrder,
  toggleSort,
  selectedItems,
  toggleItemSelection,
  selectAll,
  clearSelection,
  handleEnableDisableAccount,
  handleBulkAction,
  showFilters,
  setShowFilters
}) => {
  const [expandedUser, setExpandedUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ src: '', title: '' });

  const getStatusBadge = (user) => {
    if (user.isActive === false) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ShieldOff className="h-3 w-3 mr-1" />
          Disabled
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <Shield className="h-3 w-3 mr-1" />
        Active
      </span>
    );
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = date.toDate ? date.toDate() : new Date(date);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-IN');
    } catch (error) {
      return 'N/A';
    }
  };

  const handleViewUserDetails = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
  };

  const openImageModal = (imageSrc, title) => {
    setSelectedImage({ src: imageSrc, title });
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage({ src: '', title: '' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Users Management</h2>
          <p className="text-sm text-gray-600 mt-1">Manage client accounts and permissions</p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={() => handleBulkAction('enable_accounts')}
            disabled={selectedItems.length === 0}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-green-600 bg-green-50 border border-green-300 rounded-md hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
          >
            <UserCheck className="h-4 w-4" />
            <span>Enable Selected</span>
          </button>
          <button
            onClick={() => handleBulkAction('disable_accounts')}
            disabled={selectedItems.length === 0}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-red-600 bg-red-50 border border-red-300 rounded-md hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
          >
            <UserX className="h-4 w-4" />
            <span>Disable Selected</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0 lg:space-x-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users by name, email, phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Select All
              </button>
              <span className="text-gray-400">|</span>
              <button
                onClick={clearSelection}
                className="text-sm text-gray-600 hover:text-gray-800"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-4 pt-4 border-t border-gray-200"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => toggleSort(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="createdAt">Registration Date</option>
                  <option value="name">Name</option>
                  <option value="email">Email</option>
                  <option value="lastActive">Last Active</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
                <button
                  onClick={() => toggleSort(sortBy)}
                  className="w-full flex items-center justify-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <ArrowUpDown className="h-4 w-4" />
                  <span>{sortOrder === 'asc' ? 'Ascending' : 'Descending'}</span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === users.length && users.length > 0}
                    onChange={() => selectedItems.length === users.length ? clearSelection() : selectAll()}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <motion.tr
                  key={user.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedItems.some(item => item.id === user.id)}
                      onChange={() => toggleItemSelection(user, 'user')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </td>
                                     <td className="px-6 py-4 whitespace-nowrap">
                     <div className="flex items-center">
                       <div className="flex-shrink-0 h-10 w-10 relative">
                         {/* Profile Picture - Check for base64 data first */}
                         {(user.profile_picture || user.profilePicture) ? (
                           <img 
                             src={user.profile_picture || user.profilePicture} 
                             alt="Profile" 
                             className="h-10 w-10 rounded-full object-cover border border-blue-200"
                             onError={(e) => {
                               e.target.style.display = 'none';
                               e.target.nextSibling.style.display = 'flex';
                             }}
                           />
                         ) : null}
                         <div className={`h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center border border-blue-200 ${(user.profile_picture || user.profilePicture) ? 'hidden' : ''}`}>
                           <span className="text-sm font-bold text-white">
                             {(user.full_name || user.fullName || user.name || 'U').charAt(0).toUpperCase()}
                           </span>
                         </div>
                       </div>
                       <div className="ml-4">
                         <div className="text-sm font-medium text-gray-900">
                           {user.full_name || user.fullName || user.name || user.displayName || 'N/A'}
                         </div>
                         <div className="text-sm text-gray-500">
                           ID: {user.id.substring(0, 8)}...
                         </div>
                       </div>
                     </div>
                   </td>
                                     <td className="px-6 py-4 whitespace-nowrap">
                     <div className="text-sm text-gray-900">{user.email || 'N/A'}</div>
                     <div className="text-sm text-gray-500">{user.phone || user.mobileNumber || user.phoneNumber || 'N/A'}</div>
                   </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(user)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleViewUserDetails(user)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEnableDisableAccount(user.id, user.isActive !== false, 'user')}
                        className={user.isActive !== false ? "text-red-600 hover:text-red-900" : "text-green-600 hover:text-green-900"}
                      >
                        {user.isActive !== false ? <ShieldOff className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {users.length === 0 && (
          <div className="text-center py-12">
            <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No users found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria.' : 'Get started by creating a new user.'}
            </p>
          </div>
        )}
      </div>

      {/* Selected Items Summary */}
      {selectedItems.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckSquare className="h-5 w-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">
                {selectedItems.length} user{selectedItems.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <button
              onClick={clearSelection}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* User Details Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">User Details</h3>
              <button
                onClick={closeUserModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
                             {/* User Profile Section */}
               <div>
                 <h4 className="text-md font-medium text-gray-900 mb-4">Profile Information</h4>
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Full Name</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.full_name || selectedUser.fullName || selectedUser.name || selectedUser.displayName || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Email</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.email || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Mobile Number</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.phone || selectedUser.mobileNumber || selectedUser.phoneNumber || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Aadhar Number</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.aadhar_number || selectedUser.aadharNumber || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Date of Birth</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.date_of_birth || selectedUser.dateOfBirth || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">City</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.city || selectedUser.custom_city || 'N/A'}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">User ID</label>
                     <p className="mt-1 text-sm text-gray-900 font-mono">{selectedUser.id}</p>
                   </div>
                   <div>
                     <label className="block text-sm font-medium text-gray-700">Profile Complete</label>
                     <p className="mt-1 text-sm text-gray-900">{selectedUser.profile_complete ? 'Yes' : 'No'}</p>
                   </div>
                 </div>
               </div>

               {/* Profile Picture */}
               {selectedUser.profile_picture && (
                 <div>
                   <h4 className="text-md font-medium text-gray-900 mb-4">Profile Picture</h4>
                   <div className="flex items-center space-x-4">
                     <div className="relative">
                       <img 
                         src={selectedUser.profile_picture} 
                         alt="Profile" 
                         className="h-20 w-20 rounded-full object-cover border-4 border-blue-200"
                         onError={(e) => {
                           e.target.style.display = 'none';
                           e.target.nextSibling.style.display = 'flex';
                         }}
                       />
                       <div className="hidden h-20 w-20 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center border-4 border-blue-200">
                         <span className="text-xl font-bold text-white">
                           {(selectedUser.full_name || selectedUser.fullName || selectedUser.name || 'U').charAt(0).toUpperCase()}
                         </span>
                       </div>
                       <button
                         onClick={() => openImageModal(selectedUser.profile_picture, 'Profile Picture')}
                         className="absolute -bottom-1 -right-1 bg-blue-600 text-white p-1 rounded-full hover:bg-blue-700 transition-colors"
                         title="View Full Size"
                       >
                         <Eye className="h-3 w-3" />
                       </button>
                     </div>
                   </div>
                 </div>
               )}

               {/* Bio */}
               {selectedUser.bio && (
                 <div>
                   <h4 className="text-md font-medium text-gray-900 mb-4">Bio</h4>
                   <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                     {selectedUser.bio}
                   </p>
                 </div>
               )}

              {/* Account Status Section */}
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-4">Account Status</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1">
                      {getStatusBadge(selectedUser)}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Registration Date</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedUser.createdAt)}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Last Active</label>
                    <p className="mt-1 text-sm text-gray-900">{formatDate(selectedUser.lastActive) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Account Type</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedUser.role || selectedUser.accountType || 'Client'}</p>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              {selectedUser.address && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Address Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Address</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.address || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">City</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.city || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">State</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.state || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Pincode</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.pincode || selectedUser.zipCode || 'N/A'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Preferences */}
              {selectedUser.preferences && (
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Preferences</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Language</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.preferences.language || selectedUser.language || 'English'}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Theme</label>
                      <p className="mt-1 text-sm text-gray-900">{selectedUser.preferences.theme || selectedUser.theme || 'Light'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={closeUserModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleEnableDisableAccount(selectedUser.id, selectedUser.isActive !== false, 'user');
                  closeUserModal();
                }}
                className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 ${
                  selectedUser.isActive !== false 
                    ? 'bg-red-600 hover:bg-red-700 focus:ring-red-500' 
                    : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                }`}
              >
                {selectedUser.isActive !== false ? 'Disable Account' : 'Enable Account'}
              </button>
            </div>
                     </div>
         </div>
       )}

       {/* Image Modal */}
       {showImageModal && selectedImage.src && (
         <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-hidden">
             <div className="flex items-center justify-between p-4 border-b border-gray-200">
               <h3 className="text-lg font-semibold text-gray-900">{selectedImage.title}</h3>
               <div className="flex items-center space-x-2">
                 <button
                   onClick={() => {
                     const link = document.createElement('a');
                     link.href = selectedImage.src;
                     link.download = `${selectedImage.title.toLowerCase().replace(/\s+/g, '-')}.jpg`;
                     document.body.appendChild(link);
                     link.click();
                     document.body.removeChild(link);
                   }}
                   className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                 >
                   Download
                 </button>
                 <button
                   onClick={closeImageModal}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <X className="h-6 w-6" />
                 </button>
               </div>
             </div>
             <div className="p-4 flex items-center justify-center bg-gray-50 min-h-[60vh]">
               <img 
                 src={selectedImage.src} 
                 alt={selectedImage.title}
                 className="max-w-full max-h-[70vh] object-contain rounded border border-gray-200"
                 onError={(e) => {
                   e.target.style.display = 'none';
                   e.target.nextSibling.style.display = 'flex';
                 }}
               />
               <div className="hidden items-center justify-center w-full h-64 bg-gray-200 rounded border border-gray-200">
                 <span className="text-gray-500">Unable to load image</span>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

// Lawyers Tab Component
export const LawyersTab = ({
  lawyers,
  searchTerm,
  setSearchTerm,
  filterType,
  setFilterType,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  selectedItems,
  setSelectedItems,
  showFilters,
  setShowFilters,
  onVerifyLawyer,
  onBulkAction,
  onRefresh
}) => {
  const [expandedLawyer, setExpandedLawyer] = useState(null);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [showLawyerModal, setShowLawyerModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState({ src: '', title: '' });

  const getStatusBadge = (lawyer) => {
    const status = lawyer.verificationStatus || lawyer.status || 'pending';
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </span>
        );
      case 'pending':
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <AlertTriangle className="h-3 w-3 mr-1" />
            Pending
          </span>
        );
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    try {
      const d = new Date(date);
      if (isNaN(d.getTime())) return 'N/A';
      return d.toLocaleDateString('en-IN');
    } catch (error) {
      return 'N/A';
    }
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const toggleItemSelection = (lawyer) => {
    setSelectedItems(prev => {
      const exists = prev.find(item => item === lawyer.id);
      if (exists) {
        return prev.filter(item => item !== lawyer.id);
      } else {
        return [...prev, lawyer.id];
      }
    });
  };

  const selectAll = () => {
    setSelectedItems(lawyers.map(lawyer => lawyer.id));
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const handleVerify = (lawyerId, approved) => {
    onVerifyLawyer(lawyerId, approved);
  };

  // Debug function to log lawyer data structure
  const debugLawyerData = (lawyer) => {
    console.log('🔍 Lawyer Data Structure:', {
      fees: lawyer.fees,
      experience_years: lawyer.experience_years,
      experiences: lawyer.experiences,
      bank: lawyer.bank,
      bank_details: lawyer.bank_details,
      emergency_available: lawyer.emergency_available,
      emergency_consultation_available: lawyer.emergency_consultation_available,
      specialization: lawyer.specialization,
      case_types: lawyer.case_types
    });
  };

  const handleBulkVerify = (approved) => {
    if (selectedItems.length === 0) {
      toast.error('Please select lawyers first');
      return;
    }
    onBulkAction(approved ? 'verify' : 'reject', selectedItems);
  };

  const handleViewDetails = (lawyer) => {
    setSelectedLawyer(lawyer);
    setShowLawyerModal(true);
    debugLawyerData(lawyer); // Debug the lawyer data structure
  };

  const closeModal = () => {
    setShowLawyerModal(false);
    setSelectedLawyer(null);
  };

  const openImageModal = (imageSrc, title) => {
    setSelectedImage({ src: imageSrc, title });
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImage({ src: '', title: '' });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Lawyers</h2>
          <p className="text-sm text-gray-500">
            Manage lawyer profiles and verification status
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={onRefresh}
            disabled={false}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search lawyers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <Filter className="h-4 w-4" />
            <span>Filters</span>
          </button>
        </div>

        {/* Bulk Actions */}
        {selectedItems.length > 0 && (
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handleBulkVerify(true)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <CheckCircle className="h-4 w-4" />
              <span>Approve Selected</span>
            </button>
            <button
              onClick={() => handleBulkVerify(false)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              <XCircle className="h-4 w-4" />
              <span>Reject Selected</span>
            </button>
          </div>
        )}
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="bg-gray-50 rounded-lg p-4 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="createdAt">Registration Date</option>
                <option value="name">Name</option>
                <option value="email">Email</option>
                <option value="status">Status</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Order</label>
              <button
                onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                className="w-full flex items-center justify-center space-x-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <ArrowUpDown className="h-4 w-4" />
                <span>{sortOrder === 'asc' ? 'Ascending' : 'Descending'}</span>
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {/* Lawyers Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedItems.length === lawyers.length && lawyers.length > 0}
                    onChange={() => selectedItems.length === lawyers.length ? clearSelection() : selectAll()}
                    className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Lawyer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Specialization
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Registered
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {lawyers.map((lawyer) => (
                <motion.tr
                  key={lawyer.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-4 py-3 whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={selectedItems.includes(lawyer.id)}
                      onChange={() => toggleItemSelection(lawyer)}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8 relative">
                        {/* Profile Picture - Check for base64 data first */}
                        {(lawyer.profile_pic || lawyer.profilePicture || lawyer.profile_picture) ? (
                          <img 
                            src={lawyer.profile_pic || lawyer.profilePicture || lawyer.profile_picture} 
                            alt="Profile" 
                            className="h-8 w-8 rounded-full object-cover border border-purple-200"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div className={`h-8 w-8 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center border border-purple-200 ${(lawyer.profile_pic || lawyer.profilePicture || lawyer.profile_picture) ? 'hidden' : ''}`}>
                          <span className="text-xs font-bold text-white">
                            {(lawyer.full_name || lawyer.fullName || lawyer.user_profiles?.full_name || lawyer.name || 'L').charAt(0).toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                          {lawyer.full_name || lawyer.fullName || lawyer.user_profiles?.full_name || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500 truncate max-w-[120px]">
                          ID: {lawyer.id.substring(0, 8)}...
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 truncate max-w-[150px]">{lawyer.email || lawyer.user_profiles?.email || 'N/A'}</div>
                    <div className="text-xs text-gray-500 truncate max-w-[150px]">{lawyer.phone || lawyer.mobileNumber || lawyer.user_profiles?.phone || 'N/A'}</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="text-sm text-gray-900 truncate max-w-[120px]">{lawyer.specialization || 'N/A'}</div>
                    <div className="text-xs text-gray-500">{lawyer.experience_years || 0} years exp.</div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {getStatusBadge(lawyer)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(lawyer.createdAt || lawyer.created_at)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                   <div className="flex items-center space-x-2">
                     <button
                       onClick={() => handleViewDetails(lawyer)}
                        className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50 transition-colors"
                        title="View Details"
                     >
                       <Eye className="h-4 w-4" />
                     </button>
                     
                                             {(lawyer.verificationStatus || lawyer.status) === 'pending' && (
                         <>
                           <button
                             onClick={() => handleVerify(lawyer.id, true)}
                             className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                             title="Approve Lawyer"
                           >
                             <CheckCircle className="h-4 w-4" />
                           </button>
                           <button
                             onClick={() => handleVerify(lawyer.id, false)}
                             className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                             title="Reject Lawyer"
                           >
                             <XCircle className="h-4 w-4" />
                           </button>
                         </>
                       )}
                       
                       {(lawyer.verificationStatus || lawyer.status) === 'rejected' && (
                         <button
                           onClick={() => handleVerify(lawyer.id, true)}
                           className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50 transition-colors"
                           title="Approve"
                         >
                           <CheckCircle className="h-4 w-4" />
                         </button>
                       )}
                       
                       {(lawyer.verificationStatus || lawyer.status) === 'approved' && (
                         <button
                           onClick={() => handleVerify(lawyer.id, false)}
                           className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50 transition-colors"
                           title="Reject"
                         >
                           <XCircle className="h-4 w-4" />
                         </button>
                       )}
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {lawyers.length === 0 && (
          <div className="text-center py-12">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No lawyers found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? 'Try adjusting your search criteria.' : 'No lawyer profiles have been submitted yet.'}
            </p>
          </div>
        )}
      </div>

      {/* Selected Items Summary */}
      {selectedItems.length > 0 && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <CheckSquare className="h-5 w-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-900">
                {selectedItems.length} lawyer{selectedItems.length !== 1 ? 's' : ''} selected
              </span>
            </div>
            <button
              onClick={clearSelection}
              className="text-sm text-purple-600 hover:text-purple-800"
            >
              Clear selection
            </button>
          </div>
        </div>
      )}

      {/* Lawyer Details Modal */}
      {showLawyerModal && selectedLawyer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Lawyer Profile Details</h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Profile Picture */}
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 h-20 w-20 relative">
                  {(selectedLawyer.profile_pic || selectedLawyer.profilePicture || selectedLawyer.profile_picture) ? (
                    <img 
                      src={selectedLawyer.profile_pic || selectedLawyer.profilePicture || selectedLawyer.profile_picture} 
                      alt="Profile" 
                      className="h-20 w-20 rounded-full object-cover border-4 border-purple-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`h-20 w-20 rounded-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center border-4 border-purple-200 ${(selectedLawyer.profile_pic || selectedLawyer.profilePicture || selectedLawyer.profile_picture) ? 'hidden' : ''}`}>
                    <span className="text-2xl font-bold text-white">
                      {(selectedLawyer.full_name || selectedLawyer.fullName || selectedLawyer.user_profiles?.full_name || selectedLawyer.name || 'L').charAt(0).toUpperCase()}
                    </span>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedLawyer.full_name || selectedLawyer.fullName || selectedLawyer.user_profiles?.full_name || 'N/A'}
                  </h3>
                  <p className="text-sm text-gray-500">ID: {selectedLawyer.id}</p>
                  <div className="mt-2">
                    {getStatusBadge(selectedLawyer)}
                  </div>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedLawyer.email || 'N/A'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedLawyer.phone || 'N/A'}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-600">{selectedLawyer.location || selectedLawyer.bar_council_state || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Professional Details</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-xs text-gray-500">Specialization</span>
                      <p className="text-sm text-gray-900">{selectedLawyer.specialization || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Experience</span>
                      <p className="text-sm text-gray-900">
                        {selectedLawyer.experience_years || 
                         selectedLawyer.experiences?.[0]?.y || 
                         selectedLawyer.experiences?.[0]?.yearsOfPractice || 
                         0} years
                      </p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Bar Council License</span>
                      <p className="text-sm text-gray-900">{selectedLawyer.bar_council_number || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Consultation Fee</span>
                      <p className="text-sm text-gray-900">₹{selectedLawyer.fees?.consultation || selectedLawyer.consultation_fee || 0}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedLawyer.bio && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-2">Professional Bio</h4>
                  <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                    {selectedLawyer.bio}
                  </p>
                </div>
              )}

              {/* Education */}
              {selectedLawyer.education && selectedLawyer.education.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Education</h4>
                  <div className="space-y-2">
                                         {selectedLawyer.education.map((edu, index) => (
                       <div key={index} className="bg-gray-50 p-3 rounded-md">
                         <p className="text-sm font-medium text-gray-900">
                           {edu.qualification || edu.q || 'N/A'}
                         </p>
                         <p className="text-xs text-gray-600">
                           {edu.degree_college || edu.c || 'N/A'}
                         </p>
                         <p className="text-xs text-gray-500">
                           {edu.year_of_passing || edu.y || 'N/A'}
                         </p>
                       </div>
                     ))}
                  </div>
                </div>
              )}

              {/* Experience */}
              {selectedLawyer.experiences && selectedLawyer.experiences.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Experience</h4>
                  <div className="space-y-2">
                                         {selectedLawyer.experiences.map((exp, index) => (
                       <div key={index} className="bg-gray-50 p-3 rounded-md">
                         <p className="text-sm font-medium text-gray-900">
                           {exp.position || exp.p || exp.position || 'N/A'}
                         </p>
                         <p className="text-xs text-gray-600">
                           {exp.company || exp.f || exp.firm || 'N/A'}
                         </p>
                         <p className="text-xs text-gray-500">
                           {exp.yearsOfPractice || exp.y || exp.yearsOfPractice || 0} years
                         </p>
                         {(exp.subPracticeArea || exp.s || exp.subPracticeArea) && (
                           <p className="text-xs text-gray-500 mt-1">
                             <span className="font-medium">Sub Practice Area:</span> {exp.subPracticeArea || exp.s || exp.subPracticeArea}
                           </p>
                         )}
                       </div>
                     ))}
                  </div>
                </div>
              )}

              {/* Bank Details */}
              {(selectedLawyer.bank_details || selectedLawyer.bank) && (
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Bank Details</h4>
                  <div className="bg-gray-50 p-3 rounded-md space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Account Holder:</span>
                      <span className="text-xs font-medium text-gray-900">{selectedLawyer.bank?.name || selectedLawyer.bank_details?.account_holder_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Account Number:</span>
                      <span className="text-xs font-medium text-gray-900">{selectedLawyer.bank?.acc || selectedLawyer.bank_details?.account_number || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Bank Name:</span>
                      <span className="text-xs font-medium text-gray-900">{selectedLawyer.bank?.bank || selectedLawyer.bank_details?.bank_name || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">IFSC Code:</span>
                      <span className="text-xs font-medium text-gray-900">{selectedLawyer.bank?.ifsc || selectedLawyer.bank_details?.ifsc_code || 'N/A'}</span>
                    </div>
                  </div>
                </div>
              )}

                             {/* Languages Spoken */}
               {selectedLawyer.languages && selectedLawyer.languages.length > 0 && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-3">Languages Spoken</h4>
                   <div className="flex flex-wrap gap-2">
                     {selectedLawyer.languages.map((language, index) => (
                       <span key={index} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                         {language}
                       </span>
                     ))}
                   </div>
                 </div>
               )}

               {/* Year of Passing */}
               {selectedLawyer.year_of_passing && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-2">Year of Passing</h4>
                   <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                     {selectedLawyer.year_of_passing}
                   </p>
                 </div>
               )}

               {/* Chamber Address */}
               {selectedLawyer.chamber_address && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-2">Chamber Address</h4>
                   <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-md">
                     {selectedLawyer.chamber_address}
                   </p>
                 </div>
               )}

               {/* Availability */}
               {selectedLawyer.availability && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-3">Availability</h4>
                   <div className="bg-gray-50 p-3 rounded-md">
                     <div className="mb-3">
                       <span className="text-xs text-gray-500">Available Days:</span>
                       <div className="flex flex-wrap gap-2 mt-1">
                         {selectedLawyer.availability.available_days && selectedLawyer.availability.available_days.map((day, index) => (
                           <span key={index} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                             {day}
                           </span>
                         ))}
                       </div>
                     </div>
                     {selectedLawyer.availability.time_slots && Object.keys(selectedLawyer.availability.time_slots).length > 0 && (
                       <div>
                         <span className="text-xs text-gray-500">Time Slots:</span>
                         <div className="mt-1 space-y-1">
                           {Object.entries(selectedLawyer.availability.time_slots).map(([day, slots]) => (
                             <div key={day} className="text-xs">
                               <span className="font-medium text-gray-700">{day}:</span> {slots}
                             </div>
                           ))}
                         </div>
                       </div>
                     )}
                   </div>
                 </div>
               )}

               {/* Emergency Consultation */}
               <div>
                 <h4 className="text-sm font-medium text-gray-900 mb-3">Emergency Consultation</h4>
                 <div className="grid grid-cols-2 gap-3">
                   <div className="bg-gray-50 p-3 rounded-md">
                     <span className="text-xs text-gray-500">Available</span>
                     <p className="text-sm font-medium text-gray-900">
                       {selectedLawyer.emergency_available || selectedLawyer.emergency_consultation_available ? 'Yes' : 'No'}
                     </p>
                   </div>
                   {(selectedLawyer.emergency_available || selectedLawyer.emergency_consultation_available) && (
                     <div className="bg-gray-50 p-3 rounded-md">
                       <span className="text-xs text-gray-500">Emergency Fee</span>
                       <p className="text-sm font-medium text-gray-900">₹{selectedLawyer.fees?.emergency || selectedLawyer.emergency_consultation_fee || 0}</p>
                     </div>
                   )}
                 </div>
               </div>

               {/* File Reading Charges */}
               {selectedLawyer.file_reading_charges && selectedLawyer.file_reading_charges.length > 0 && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-3">File Reading Charges</h4>
                   <div className="bg-gray-50 p-3 rounded-md space-y-2">
                     {selectedLawyer.file_reading_charges.map((charge, index) => (
                       <div key={index} className="flex justify-between items-center">
                         <span className="text-xs text-gray-600">
                           {charge.from_page} - {charge.to_page} pages
                         </span>
                         <span className="text-sm font-medium text-gray-900">₹{charge.amount}</span>
                       </div>
                     ))}
                   </div>
                 </div>
               )}

               {/* Achievements */}
               {selectedLawyer.achievements && selectedLawyer.achievements.length > 0 && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-2">Achievements</h4>
                   <div className="space-y-2">
                                            {Array.isArray(selectedLawyer.achievements) ? (
                         selectedLawyer.achievements.map((achievement, index) => (
                           <div key={index} className="bg-gray-50 p-3 rounded-md">
                             <p className="text-sm text-gray-600">
                               {typeof achievement === 'object' 
                                 ? (achievement.text || achievement.achievement || achievement.name || JSON.stringify(achievement))
                                 : achievement}
                             </p>
                           </div>
                         ))
                       ) : (
                       <div className="bg-gray-50 p-3 rounded-md">
                         <p className="text-sm text-gray-600">
                           {selectedLawyer.achievements}
                         </p>
                       </div>
                     )}
                   </div>
                 </div>
               )}

               {/* Social Links */}
               {(selectedLawyer.linkedin || selectedLawyer.facebook || selectedLawyer.instagram || selectedLawyer.twitter) && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-3">Social Links</h4>
                   <div className="grid grid-cols-2 gap-3">
                     {selectedLawyer.linkedin && (
                       <div className="bg-gray-50 p-3 rounded-md">
                         <span className="text-xs text-gray-500">LinkedIn</span>
                         <a 
                           href={selectedLawyer.linkedin} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-sm text-blue-600 hover:text-blue-800 block truncate"
                         >
                           {selectedLawyer.linkedin}
                         </a>
                       </div>
                     )}
                     {selectedLawyer.facebook && (
                       <div className="bg-gray-50 p-3 rounded-md">
                         <span className="text-xs text-gray-500">Facebook</span>
                         <a 
                           href={selectedLawyer.facebook} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-sm text-blue-600 hover:text-blue-800 block truncate"
                         >
                           {selectedLawyer.facebook}
                         </a>
                       </div>
                     )}
                     {selectedLawyer.instagram && (
                       <div className="bg-gray-50 p-3 rounded-md">
                         <span className="text-xs text-gray-500">Instagram</span>
                         <a 
                           href={selectedLawyer.instagram} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-sm text-blue-600 hover:text-blue-800 block truncate"
                         >
                           {selectedLawyer.instagram}
                         </a>
                       </div>
                     )}
                     {selectedLawyer.twitter && (
                       <div className="bg-gray-50 p-3 rounded-md">
                         <span className="text-xs text-gray-500">Twitter</span>
                         <a 
                           href={selectedLawyer.twitter} 
                           target="_blank" 
                           rel="noopener noreferrer"
                           className="text-sm text-blue-600 hover:text-blue-800 block truncate"
                         >
                           {selectedLawyer.twitter}
                         </a>
                       </div>
                     )}
                   </div>
                 </div>
               )}

                               {/* Sub Practice Areas */}
                {(selectedLawyer.case_types && selectedLawyer.case_types.length > 0) || 
                 (selectedLawyer.experiences && selectedLawyer.experiences.some(exp => exp.subPracticeArea || exp.s)) ||
                 (selectedLawyer.specialization && Array.isArray(selectedLawyer.specialization) && selectedLawyer.specialization.length > 0) && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Sub Practice Areas</h4>
                    <div className="flex flex-wrap gap-2">
                      {/* From case_types if available */}
                      {selectedLawyer.case_types && selectedLawyer.case_types.map((caseType, index) => (
                        <span key={`case-${index}`} className="px-3 py-1 bg-purple-100 text-purple-800 text-xs rounded-full">
                          {caseType}
                        </span>
                      ))}
                      {/* From experiences sub practice areas */}
                      {selectedLawyer.experiences && selectedLawyer.experiences
                        .filter(exp => exp.subPracticeArea || exp.s)
                        .map((exp, index) => (
                          <span key={`exp-${index}`} className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {exp.subPracticeArea || exp.s}
                          </span>
                        ))}
                      {/* From specialization array */}
                      {selectedLawyer.specialization && Array.isArray(selectedLawyer.specialization) && 
                       selectedLawyer.specialization.map((spec, index) => (
                         <span key={`spec-${index}`} className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                           {typeof spec === 'string' ? spec : spec.name || spec}
                         </span>
                       ))}
                    </div>
                  </div>
                )}

               {/* Number of Cases Handled */}
               {selectedLawyer.no_of_cases_handled && (
                 <div>
                   <h4 className="text-sm font-medium text-gray-900 mb-2">Cases Handled</h4>
                   <div className="bg-gray-50 p-3 rounded-md">
                     <span className="text-sm font-medium text-gray-900">{selectedLawyer.no_of_cases_handled} cases</span>
                   </div>
                 </div>
               )}

               {/* Consultation Fees */}
               <div>
                 <h4 className="text-sm font-medium text-gray-900 mb-3">Consultation Fees</h4>
                 <div className="grid grid-cols-2 gap-3">
                   <div className="bg-gray-50 p-3 rounded-md">
                     <span className="text-xs text-gray-500">Text Consultation</span>
                     <p className="text-sm font-medium text-gray-900">₹{selectedLawyer.fees?.text || selectedLawyer.text_consultation_fee || 0}</p>
                   </div>
                   <div className="bg-gray-50 p-3 rounded-md">
                     <span className="text-xs text-gray-500">Call Consultation</span>
                     <p className="text-sm font-medium text-gray-900">₹{selectedLawyer.fees?.call || selectedLawyer.call_consultation_fee || 0}</p>
                   </div>
                   <div className="bg-gray-50 p-3 rounded-md">
                     <span className="text-xs text-gray-500">Video Call</span>
                     <p className="text-sm font-medium text-gray-900">₹{selectedLawyer.fees?.video || selectedLawyer.video_call_consultation_fee || 0}</p>
                   </div>
                   <div className="bg-gray-50 p-3 rounded-md">
                     <span className="text-xs text-gray-500">In-Person</span>
                     <p className="text-sm font-medium text-gray-900">₹{selectedLawyer.fees?.in_person || selectedLawyer.in_person_consultation_fee || 0}</p>
                   </div>
                 </div>
               </div>

                             {/* Registration Date */}
               <div>
                 <h4 className="text-sm font-medium text-gray-900 mb-2">Registration Information</h4>
                 <div className="flex items-center space-x-2">
                   <Calendar className="h-4 w-4 text-gray-400" />
                   <span className="text-sm text-gray-600">
                     Registered on {formatDate(selectedLawyer.createdAt || selectedLawyer.created_at)}
                   </span>
                 </div>
               </div>



              {/* Verification Documents */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Verification Documents</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* ID Proof */}
                  {selectedLawyer.id_proof && (
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">
                          ID Proof ({selectedLawyer.id_proof_type || 'Document'})
                        </span>
                        <button
                          onClick={() => openImageModal(selectedLawyer.id_proof, `ID Proof - ${selectedLawyer.id_proof_type || 'Document'}`)}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Full Size
                        </button>
                      </div>
                      <img 
                        src={selectedLawyer.id_proof} 
                        alt="ID Proof" 
                        className="w-full h-32 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-32 bg-gray-200 rounded border border-gray-200 items-center justify-center">
                        <span className="text-xs text-gray-500">ID Proof Image</span>
                      </div>
                    </div>
                  )}

                  {/* Bar Council Certificate */}
                  {selectedLawyer.bar_council_certificate && (
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">
                          Bar Council Certificate
                        </span>
                        <button
                          onClick={() => openImageModal(selectedLawyer.bar_council_certificate, 'Bar Council Certificate')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Full Size
                        </button>
                      </div>
                      <img 
                        src={selectedLawyer.bar_council_certificate} 
                        alt="Bar Council Certificate" 
                        className="w-full h-32 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-32 bg-gray-200 rounded border border-gray-200 items-center justify-center">
                        <span className="text-xs text-gray-500">Bar Council Certificate</span>
                      </div>
                    </div>
                  )}

                  {/* Selfie with ID */}
                  {selectedLawyer.selfie_with_id && (
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">
                          Selfie with ID
                        </span>
                        <button
                          onClick={() => openImageModal(selectedLawyer.selfie_with_id, 'Selfie with ID')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Full Size
                        </button>
                      </div>
                      <img 
                        src={selectedLawyer.selfie_with_id} 
                        alt="Selfie with ID" 
                        className="w-full h-32 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-32 bg-gray-200 rounded border border-gray-200 items-center justify-center">
                        <span className="text-xs text-gray-500">Selfie with ID</span>
                      </div>
                    </div>
                  )}

                  {/* Face Verification Image */}
                  {selectedLawyer.face_verification_image && (
                    <div className="bg-gray-50 p-3 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">
                          Face Verification
                        </span>
                        <button
                          onClick={() => openImageModal(selectedLawyer.face_verification_image, 'Face Verification')}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Full Size
                        </button>
                      </div>
                      <img 
                        src={selectedLawyer.face_verification_image} 
                        alt="Face Verification" 
                        className="w-full h-32 object-cover rounded border border-gray-200"
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                      <div className="hidden w-full h-32 bg-gray-200 rounded border border-gray-200 items-center justify-center">
                        <span className="text-xs text-gray-500">Face Verification</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Document Status Summary */}
                <div className="mt-4 p-3 bg-blue-50 rounded-md">
                  <h5 className="text-xs font-medium text-blue-900 mb-2">Document Verification Status</h5>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${selectedLawyer.id_proof ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={selectedLawyer.id_proof ? 'text-green-700' : 'text-red-700'}>
                        ID Proof: {selectedLawyer.id_proof ? '✓ Uploaded' : '✗ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${selectedLawyer.bar_council_certificate ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={selectedLawyer.bar_council_certificate ? 'text-green-700' : 'text-red-700'}>
                        Bar Council Cert: {selectedLawyer.bar_council_certificate ? '✓ Uploaded' : '✗ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${selectedLawyer.selfie_with_id ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={selectedLawyer.selfie_with_id ? 'text-green-700' : 'text-red-700'}>
                        Selfie with ID: {selectedLawyer.selfie_with_id ? '✓ Uploaded' : '✗ Missing'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className={`w-2 h-2 rounded-full ${selectedLawyer.face_verification_image ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={selectedLawyer.face_verification_image ? 'text-green-700' : 'text-red-700'}>
                        Face Verification: {selectedLawyer.face_verification_image ? '✓ Uploaded' : '✗ Missing'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Close
              </button>
              {(selectedLawyer.verificationStatus || selectedLawyer.status) === 'pending' && (
                <>
                  <button
                    onClick={() => {
                      handleVerify(selectedLawyer.id, true);
                      closeModal();
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => {
                      handleVerify(selectedLawyer.id, false);
                      closeModal();
                    }}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && selectedImage.src && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[95vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{selectedImage.title}</h3>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = selectedImage.src;
                    link.download = `${selectedImage.title.toLowerCase().replace(/\s+/g, '-')}.jpg`;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                  className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Download
                </button>
                <button
                  onClick={closeImageModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-4 flex items-center justify-center bg-gray-50 min-h-[60vh]">
              <img 
                src={selectedImage.src} 
                alt={selectedImage.title}
                className="max-w-full max-h-[70vh] object-contain rounded border border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="hidden items-center justify-center w-full h-64 bg-gray-200 rounded border border-gray-200">
                <span className="text-gray-500">Unable to load image</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};